'use client';

import { useState, useRef, useEffect } from 'react';
import { Info, ChevronLeft, ChevronRight } from 'lucide-react';
import { PricingSectionCheck as Icon_PricingSectionCheck } from "@/components/customer/icons/PricingSectionCheck";
const SvgIcons = { PricingSectionCheck: Icon_PricingSectionCheck };

const PricingSection = () => {
  const [currentMobileIndex, setCurrentMobileIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const carouselRef = useRef(null);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  const plans = [
    {
      name: 'Starter',
      price: '₹5000',
      period: '/ Month',
      description: 'Perfect for testing campaigns & learning your audience.',
      buttonText: 'Start Plan',
      buttonVariant: 'outline',
      popular: false,
      isPremium: false,
      features: [
        'Target audience research',
        'Target audience segmentation',
        'Campaign strategy & optimization',
        'Monthly analytics & ROI reports',
        'Retargeting & conversion optimization',
      ],
    },
    {
      name: 'Essential',
      price: '₹20,000',
      period: '/ Month',
      description: 'Grow your reach and maintain a consistent brand voice.',
      buttonText: 'Start Plan',
      buttonVariant: 'outline',
      popular: false,
      isPremium: false,
      features: [
        'Target audience research',
        'Target audience segmentation',
        'Campaign strategy & optimization',
        'Monthly analytics & ROI reports',
        'Retargeting & conversion optimization',
        '8 posts / month',
        '4 reels / month',
        '10-12 stories / month',
        'Hashtag research & Copy writing',
        'Monthly performance report',
      ],
    },
    {
      name: 'Growth',
      price: '₹30,000',
      period: '/ Month',
      description: 'Scale faster with advanced content strategy & engagement.',
      buttonText: 'Start Plan',
      buttonVariant: 'primary',
      popular: true,
      isPremium: false,
      features: [
        'Target audience research',
        'Target audience segmentation',
        'Campaign strategy & optimization',
        'Monthly analytics & ROI reports',
        'Retargeting & conversion optimization',
        '12 posts / month',
        '8 reels / month',
        '15-20 stories / month',
        'Full caption + hashtag strategy',
        'Community engagement (DMs + comment)',
        'UGC recasts',
        'Monthly analytics & insights report',
        'Highlight covers + visual refreshes',
      ],
    },
    {
      name: 'Premium',
      price: '₹50,000',
      period: '/ Month',
      description: 'Complete brand management for high-performance marketing.',
      buttonText: 'Talk to a Strategist',
      buttonVariant: 'outline',
      popular: false,
      isPremium: true,
      features: [
        'Target audience research',
        'Target audience segmentation',
        'Campaign strategy & optimization',
        'Monthly analytics & ROI reports',
        'Retargeting & conversion optimization',
        '15-18 posts / month (carousel + static)',
        '12 reels / month with Ads progress',
        'Daily stories (polls, Q&As, Countdowns)',
        'Repurting client & UGC content',
        'Daily DM & comment handling (Mon-Sat)',
        'Monthly strategy call + detailed report',
        'Visual + messaging optimization',
        'Highlight covers + visual refreshes',
      ],
    },
  ];

  const handlePrev = () => {
    setCurrentMobileIndex((prev) => (prev === 0 ? plans.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentMobileIndex((prev) => (prev === plans.length - 1 ? 0 : prev + 1));
  };

  const onTouchStart = (e) => {
    setTouchEnd(0);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      handleNext();
    }
    if (isRightSwipe) {
      handlePrev();
    }
  };

  return (
    <div className="min-h-screen bg-[#00091E] md:py-[120px] py-[60px] px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-[38px] font-NeueMontreal lg:text-[48px] font-bold text-white mb-4">
            Choose Your Perfect{' '}
            <span className="text-[#487CFF] font-NeueMontreal">Growth Plan</span>
          </h2>
          <p className="text-[#B3B2D9] text-[16px] sm:text-[18px] font-Schibsted_Grotesk">
            Transparent pricing. Real results. Tailored to your goals.
          </p>
        </div>

        {/* Desktop Grid View - Hidden on mobile */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan, index) => (
            <PricingCard key={index} plan={plan} />
          ))}
        </div>

        {/* Mobile Carousel View - Visible only on mobile */}
        <div className="md:hidden relative">
          <div
            className="overflow-hidden"
            ref={carouselRef}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <div
              className="flex transition-transform duration-300 ease-in-out"
              style={{
                transform: `translateX(-${currentMobileIndex * 100}%)`,
              }}
            >
              {plans.map((plan, index) => (
                <div key={index} className="w-full flex-shrink-0 px-3">
                  <PricingCard plan={plan} />
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-center gap-4 mt-8">
            <button
              onClick={handlePrev}
              className="w-10 h-10 rounded-full border border-[#FFFFFF]/20 flex items-center justify-center text-[#FFFFFF]/60 hover:text-white hover:border-[#FFFFFF]/40 transition-colors"
              aria-label="Previous plan"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={handleNext}
              className="w-10 h-10 rounded-full border border-[#FFFFFF]/20 flex items-center justify-center text-[#FFFFFF]/60 hover:text-white hover:border-[#FFFFFF]/40 transition-colors"
              aria-label="Next plan"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PricingCard = ({ plan }) => {
  return (
    <div
      className={`relative rounded-[24px] p-6 ${
        plan.isPremium
          ? 'bg-[radial-gradient(101.26%_106.31%_at_17.04%_-1.07%,#091F60_0%,#0D1838_100%)]'
          : 'bg-[#0D1838]'
      } ${
        plan.popular
          ? 'border-[#195AFF] border-2 shadow-lg shadow-[#195AFF]/20'
          : plan.isPremium
          ? ''
          : 'border-transparent border-2'
      } h-full flex flex-col`}
    >
      {/* Plan Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white text-[16px] font-medium tracking-wide font-Schibsted_Grotesk">
            {plan.name}
          </h3>
          {plan.popular && (
            <div className="text-[#FFEF80] border border-[#FFEF80] text-[12px] font-semibold px-3 py-1 rounded-[4px] flex items-center gap-1 whitespace-nowrap">
              <span className="text-[#FFEF80]">★</span> Most Popular
            </div>
          )}
        </div>

        <div className="mb-3 flex items-start justify-between">
          <div className="flex items-baseline">
            <p className="text-white text-[32px] font-bold font-NeueMontreal tracking-wide leading-none">
              {plan.price}
            </p>
            <p className="text-[#FFF]/70 text-[14px] ml-1 font-Schibsted_Grotesk">
              {plan.period}
            </p>
          </div>
          <button className="text-[#FFFFFF]/40 hover:text-[#FFF]/70 transition-colors">
            <Info className="w-4 h-4" />
          </button>
        </div>

        <p className="text-[#FFF]/70 text-[14px] leading-[1.5] font-Schibsted_Grotesk">
          {plan.description}
        </p>
      </div>

      {/* CTA Button */}
      <button
        className={`w-full py-3 rounded-[8px] font-medium mb-6 transition-all font-Schibsted_Grotesk text-[15px] ${
          plan.buttonVariant === 'primary'
            ? 'bg-[#195AFF] hover:bg-[#195AFF]/80 text-white'
            : 'bg-transparent border-2 border-[#FFFFFF] hover:border-[#FFFFFF]/70 text-white'
        }`}
      >
        {plan.buttonText}
      </button>

      {/* Divider */}
      <div className="bg-[#FFFFFF] opacity-10 h-[2px] w-full mb-[24px]"></div>

      {/* Features List */}
      <div className="space-y-[14px] flex-grow">
        {plan.features.map((feature, index) => (
          <div key={index} className="flex items-start gap-3">
            <div className="flex-shrink-0 flex items-center justify-center mt-[2px]">
              <SvgIcons.PricingSectionCheck />
            </div>
            <span className="text-[#FFF] text-[14px] leading-[1.6] font-Schibsted_Grotesk">
              {feature}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PricingSection;
